import { notifications } from '$lib/stores/notifications';
import { toastMessage } from '$lib/stores/toast';
import { post } from './apis';

export const getNotifications = async (
	status: NotificationStatus[],
	pagination: Pagination
): Promise<NotificationsResult | void> => {
	const { response, json } = await post({
		query: /* GraphQL */ `
			query notifications($status: [NotificationStatus!]!, $pagination: Pagination!) {
				notifications(status: $status, pagination: $pagination) {
					total
					notifications {
						id
						createdAt
						status
						projectOwner
						details {
							... on NotificationInfo {
								type
								subject
								message
							}
							... on NotificationReceivedApproval {
								type
								objectId
								submissionId
								project
							}
							... on NotificationNewerSubmission {
								type
								submissionId
								objectId
								project
							}
						}
					}
				}
			}
		`,
		variables: {
			status,
			pagination,
		},
	});

	if (response.status === 200 && json.data) {
		return json.data.notifications;
	}
};

export const updateNotificationStatus = async (
	notificationId: string,
	status: NotificationStatus
): Promise<void> => {
	const { response, json } = await post({
		query: /* GraphQL */ `
			mutation ($notificationId: String!, $status: NotificationStatus!) {
				setNotificationStatus(notificationId: $notificationId, status: $status) {
					ok
					error
				}
			}
		`,
		variables: {
			notificationId,
			status,
		},
	});

	if (response.status === 200) {
		if (json.data.setNotificationStatus.ok) {
			const newNotifications = await getNotifications(['UNREAD', 'READ'], {
				size: 20,
				skip: 0,
			});
			if (newNotifications) {
				notifications.set(newNotifications);
			}
		}
	}
};

export const deleteAllNotifications = async (): Promise<void> => {
	const { response, json } = await post({
		query: /* GraphQL */ `
			mutation {
				setNotificationStatusForAll(status: DELETED) {
					ok
					error
				}
			}
		`,
	});

	if (response.status === 200) {
		if (json.data.setNotificationStatusForAll.ok) {
			const newNotifications = await getNotifications(['UNREAD', 'READ'], {
				size: 20,
				skip: 0,
			});
			if (newNotifications) {
				notifications.set(newNotifications);
				toastMessage.set('Alle Benachrichtigungen gelöscht');
			}
		}
	}
};

export const deleteNotification = async (notificationId: string): Promise<void> => {
	const { response, json } = await post({
		query: /* GraphQL */ `
			mutation ($notificationId: String!) {
				setNotificationStatus(notificationId: $notificationId, status: DELETED) {
					ok
					error
				}
			}
		`,
		variables: {
			notificationId,
		},
	});

	if (response.status === 200) {
		if (json.data.setNotificationStatus.ok) {
			const newNotifications = await getNotifications(['UNREAD', 'READ'], {
				size: 20,
				skip: 0,
			});
			if (newNotifications) {
				notifications.set(newNotifications);
				toastMessage.set('Benachrichtigung gelöscht');
			}
		}
	}
};

export const getDetails = async (objectId: number, submissionId: string) => {
	const { response, json } = await post({
		query: /* GraphQL */ `
			query ($objectId: Int!) {
				object(objectId: $objectId) {
					id
				}
				submissions(objectId: $objectId) {
					id
					version
					user {
						id
						name
					}
				}
			}
		`,
		variables: {
			objectId: objectId,
		},
	});
	if (response.ok) {
		const object = json.data.object as ObjectType;
		const submission = json.data.submissions.find(
			(submission) => submission.id === submissionId
		) as Submission;
		return {
			object,
			submission,
		};
	}
};
